import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiX, FiUploadCloud, FiTrash2, FiSave, FiInfo } from "react-icons/fi";
import { createGame, updateGame } from "../../../utils/gameApi";

const EMPTY_FORM = {
  name: "",
  gameKey: "",
  description: "",
  route: "",
  rewardInfo: "",
  gameTime: 30,
  minPoints: 10,
  maxPoints: 50,
  cooldownSeconds: 180,
  maxPlaysPerEvent: "",
  maxPlaysPerDay: 10,
  sortOrder: 0,
  isActive: true,
  config: "",
};

/** Mirrors the backend slug rule so the preview matches what gets saved. */
const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);

const NumberField = ({ label, value, onChange, hint, min, max, placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
    />
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

/**
 * Create or edit one game in the catalog.
 *
 * `gameKey` is shown but only editable while creating: history rows and event
 * point ledger entries are written against it, so the backend refuses to
 * change it afterwards and the field goes read-only to match.
 */
const GameModal = ({ game, onClose, onSaved }) => {
  const isEdit = Boolean(game?.id);
  const [form, setForm] = useState(EMPTY_FORM);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [removeIcon, setRemoveIcon] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [removeBanner, setRemoveBanner] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);
  const bannerInput = useRef(null);

  useEffect(() => {
    if (!game) {
      setForm(EMPTY_FORM);
      return;
    }
    setForm({
      name: game.name || "",
      gameKey: game.gameKey || "",
      description: game.description || "",
      route: game.route || "",
      rewardInfo: game.rewardInfo || "",
      gameTime: game.gameTime ?? 30,
      minPoints: game.minPoints ?? 10,
      maxPoints: game.maxPoints ?? 50,
      cooldownSeconds: game.cooldownSeconds ?? 180,
      maxPlaysPerEvent: game.maxPlaysPerEvent ?? "",
      maxPlaysPerDay: game.maxPlaysPerDay ?? 10,
      sortOrder: game.sortOrder ?? 0,
      isActive: game.isActive !== false,
      // Pretty-printed so the textarea is editable by hand. An empty config
      // stays an empty field rather than the noise of "{}".
      config:
        game.config && Object.keys(game.config).length ? JSON.stringify(game.config, null, 2) : "",
    });
    setIconFile(null);
    setIconPreview(null);
    setRemoveIcon(false);
    setBannerFile(null);
    setBannerPreview(null);
    setRemoveBanner(false);
  }, [game]);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const derivedKey = useMemo(
    () => (isEdit ? form.gameKey : slugify(form.gameKey || form.name)),
    [isEdit, form.gameKey, form.name]
  );
  const is2048 = form.gameKey === "game_2048" || derivedKey === "game_2048";

  const handleIconPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("That icon is larger than the 2MB limit");
      return;
    }

    setIconFile(file);
    setRemoveIcon(false);
    const reader = new FileReader();
    reader.onload = () => setIconPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    setRemoveIcon(true);
    if (fileInput.current) fileInput.current.value = "";
  };

  const handleBannerPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("That banner is larger than the 2MB limit");
      return;
    }

    setBannerFile(file);
    setRemoveBanner(false);
    const reader = new FileReader();
    reader.onload = () => setBannerPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearBanner = () => {
    setBannerFile(null);
    setBannerPreview(null);
    setRemoveBanner(true);
    if (bannerInput.current) bannerInput.current.value = "";
  };

  const validate = () => {
    if (!form.name.trim()) return "Game name is required.";
    if (!derivedKey) return "Could not build a game key from that name. Use letters or numbers.";
    const min = Number(form.minPoints);
    const max = Number(form.maxPoints);
    // 0 is valid: a slots or wheel game can legitimately pay nothing on a loss.
    if (min < 0) return "Minimum points cannot be negative.";
    if (max < 1) return "Maximum points must be at least 1.";
    if (min > max) return "Minimum points cannot be greater than Maximum points.";
    // Every game, 2048 included: the app is sent this as `total_play` and has
    // no way to read "unlimited".
    const perDay = Number(form.maxPlaysPerDay);
    if (form.maxPlaysPerDay === "" || !Number.isInteger(perDay) || perDay < 1) {
      return "Max plays per day is required and must be a whole number of at least 1.";
    }
    if (!is2048) {
      const time = Number(form.gameTime);
      if (time < 5 || time > 600) return "Game time must be between 5 and 600 seconds.";
      const cooldown = Number(form.cooldownSeconds);
      if (cooldown < 0 || cooldown > 86400) return "Cooldown must be between 0 and 86400 seconds.";
    }

    // Caught here rather than at the API, so a typo in a long config does not
    // cost a round trip to find.
    if (!is2048 && form.config && form.config.trim()) {
      let parsed;
      try {
        parsed = JSON.parse(form.config);
      } catch {
        return "Game config is not valid JSON.";
      }
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return "Game config must be a JSON object, not a list or a single value.";
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        iconFile,
        removeIcon,
        bannerFile,
        removeBanner,
        // An emptied textarea clears the config rather than saving "".
        config: form.config && form.config.trim() ? form.config.trim() : null,
      };
      if (is2048) {
        delete payload.gameTime;
        delete payload.cooldownSeconds;
        delete payload.maxPlaysPerEvent;
        delete payload.config;
      }
      // The key is set once, at creation. On an edit it is only displayed, so
      // it is dropped rather than posted back as a field that cannot change.
      if (isEdit) delete payload.gameKey;
      else payload.gameKey = derivedKey;

      const res = isEdit ? await updateGame(game.id, payload) : await createGame(payload);
      toast.success(res.message || (isEdit ? "Game updated" : "Game created"));
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message || "Failed to save game");
    } finally {
      setSaving(false);
    }
  };

  const currentIcon = iconPreview || (removeIcon ? null : game?.iconUrl || null);
  const currentBanner = bannerPreview || (removeBanner ? null : game?.bannerUrl || null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-full overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-800">{isEdit ? "Edit Game" : "Add Game"}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {isEdit ? "Update how this game plays and scores." : "Create a game players can start from the app."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Game Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                placeholder="e.g. Lucky Spin"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Game Key</label>
              <input
                type="text"
                value={derivedKey}
                readOnly={isEdit}
                onChange={(e) => set("gameKey")(slugify(e.target.value))}
                placeholder="auto-generated from the name"
                className={`w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                  isEdit ? "bg-gray-50 text-gray-500" : ""
                }`}
              />
              <p className="text-[11px] text-gray-400 mt-1 flex items-start gap-1">
                <FiInfo size={12} className="mt-0.5 shrink-0" />
                {isEdit
                  ? "Fixed for life: play history and event points are recorded against this key."
                  : "The app sends this key to start a session. It cannot be changed later."}
              </p>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => set("description")(e.target.value)}
                placeholder="Shown in the app game list"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">App Route</label>
              <input
                type="text"
                value={form.route}
                onChange={(e) => set("route")(e.target.value)}
                placeholder="e.g. lucky-slot"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                The screen the app opens. Defaults to the game key.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Reward Summary</label>
              <input
                type="text"
                value={form.rewardInfo}
                onChange={(e) => set("rewardInfo")(e.target.value)}
                placeholder="e.g. Hit 777 for +200 Event Points"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <p className="text-[11px] text-gray-400 mt-1">One line explaining what the game pays.</p>
            </div>
          </div>

          {/* Icon */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Game Icon</label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                {currentIcon ? (
                  <img src={currentIcon} alt="" className="w-full h-full object-cover" />
                ) : (
                  <FiUploadCloud size={20} className="text-gray-300" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                  onChange={handleIconPick}
                  className="hidden"
                  id="game-icon-input"
                />
                <label
                  htmlFor="game-icon-input"
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition"
                >
                  Choose image
                </label>
                {currentIcon && (
                  <button
                    type="button"
                    onClick={clearIcon}
                    className="px-3 py-2 text-xs font-medium rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition flex items-center gap-1"
                  >
                    <FiTrash2 size={12} /> Remove
                  </button>
                )}
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">PNG, JPG, WEBP, SVG or GIF, up to 2MB.</p>
          </div>

          {/* Banner */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Game Banner</label>
            <div className="flex items-center gap-4">
              <div className="w-28 h-16 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                {currentBanner ? (
                  <img src={currentBanner} alt="" className="w-full h-full object-cover" />
                ) : (
                  <FiUploadCloud size={20} className="text-gray-300" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={bannerInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                  onChange={handleBannerPick}
                  className="hidden"
                  id="game-banner-input"
                />
                <label
                  htmlFor="game-banner-input"
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition"
                >
                  Choose image
                </label>
                {currentBanner && (
                  <button
                    type="button"
                    onClick={clearBanner}
                    className="px-3 py-2 text-xs font-medium rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition flex items-center gap-1"
                  >
                    <FiTrash2 size={12} /> Remove
                  </button>
                )}
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">Optional wide image for the game's own screen.</p>
          </div>

          {/* Mechanics */}
          {!is2048 && <div className="border-t border-gray-100 pt-5">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Play Mechanics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NumberField
                label="Game Time (seconds)"
                value={form.gameTime}
                onChange={set("gameTime")}
                min={5}
                max={600}
                hint="How long one round runs."
              />
              <NumberField
                label="Break Time (seconds)"
                value={form.cooldownSeconds}
                onChange={set("cooldownSeconds")}
                min={0}
                max={86400}
                hint="Wait after a round before playing again. 0 for none."
              />
              <NumberField
                label="Minimum Points"
                value={form.minPoints}
                onChange={set("minPoints")}
                min={1}
                hint="Low end of the random target."
              />
              <NumberField
                label="Maximum Points"
                value={form.maxPoints}
                onChange={set("maxPoints")}
                min={1}
                hint="High end of the random target."
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-3 flex items-start gap-1">
              <FiInfo size={12} className="mt-0.5 shrink-0" />
              Each round rolls a target somewhere in this range. A player can never be awarded more than the roll,
              and claiming more disqualifies the round.
            </p>
          </div>}

          {/* Limits */}
          <div className="border-t border-gray-100 pt-5">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Play Limits</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {!is2048 && <NumberField
                label="Max Plays Per Event"
                value={form.maxPlaysPerEvent}
                onChange={set("maxPlaysPerEvent")}
                min={1}
                placeholder="Unlimited"
                hint="Resets when a new event starts."
              />}
              <NumberField
                label="Max Plays Per Day"
                value={form.maxPlaysPerDay}
                onChange={set("maxPlaysPerDay")}
                min={1}
                placeholder="10"
                hint="Required. Counted when a round is submitted; resets at 00:00 UTC."
              />
              {is2048 && <p className="text-sm text-emerald-600 self-center">Unlimited plays per event</p>}
              <NumberField
                label="Sort Order"
                value={form.sortOrder}
                onChange={set("sortOrder")}
                min={0}
                hint="Lower shows first in the app."
              />
            </div>
          </div>

          {/* Game-specific config */}
          {!is2048 && <div className="border-t border-gray-100 pt-5">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">
              Game-Specific Settings
            </h3>
            <textarea
              rows={10}
              value={form.config}
              onChange={(e) => set("config")(e.target.value)}
              spellCheck={false}
              placeholder={'{\n  "totalSpins": 5,\n  "tiers": { "jackpot": 200, "match2": 10 }\n}'}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <p className="text-[11px] text-gray-400 mt-1 flex items-start gap-1">
              <FiInfo size={12} className="mt-0.5 shrink-0" />
              Anything only this game understands: slot tiers and odds, wheel slices, a points table. The app
              receives it as the <span className="font-mono">config</span> object on this game, untouched. Must be
              a JSON object. Leave empty if the game needs none.
            </p>
          </div>}

          {/* Status */}
          <div className="border-t border-gray-100 pt-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Active</p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                Only active games appear in the app game list.
              </p>
            </div>
            <button
              type="button"
              onClick={() => set("isActive")(!form.isActive)}
              className={`relative w-11 h-6 rounded-full transition ${form.isActive ? "bg-emerald-500" : "bg-gray-300"}`}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${
                  form.isActive ? "left-[22px]" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-white transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition flex items-center gap-2"
          >
            <FiSave size={14} />
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Game"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameModal;
